/**
 * Google reCAPTCHA Enterprise helper (invisible, score-based).
 *
 * The site key is public by design and is injected by the shortcode as
 * window.wpApiSettings.recaptchaSiteKey. When it is absent (not configured)
 * every function here is a no-op so the tool keeps working.
 */

const ACTION = "id_generator";
const SCRIPT_ID = "idg-recaptcha";
let loadPromise = null;

export function recaptchaSiteKey() {
	return (window.wpApiSettings && window.wpApiSettings.recaptchaSiteKey) || "";
}

export function isRecaptchaEnabled() {
	return recaptchaSiteKey() !== "";
}

function api() {
	return (window.grecaptcha && window.grecaptcha.enterprise) || null;
}

export function loadRecaptcha() {
	const key = recaptchaSiteKey();
	if (!key) {
		return Promise.resolve(null);
	}
	if (api() && api().execute) {
		return Promise.resolve(api());
	}
	if (loadPromise) {
		return loadPromise;
	}
	loadPromise = new Promise((resolve, reject) => {
		const existing = document.getElementById(SCRIPT_ID);
		const script = existing || document.createElement("script");
		if (!existing) {
			script.id = SCRIPT_ID;
			script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(key)}`;
			script.async = true;
			script.defer = true;
			// Cookiebot: this script is essential to the tool working.
			script.setAttribute("data-cookieconsent", "ignore");
		}
		script.addEventListener("load", () => {
			const g = api();
			if (g && g.ready) {
				g.ready(() => resolve(g));
			} else {
				reject(new Error("reCAPTCHA failed to initialise"));
			}
		});
		script.addEventListener("error", () => {
			loadPromise = null;
			reject(new Error("reCAPTCHA script failed to load"));
		});
		if (!existing) {
			document.head.appendChild(script);
		}
	});
	return loadPromise;
}

/**
 * Returns a fresh token, or "" when reCAPTCHA is not configured or unavailable.
 * The backend decides whether a missing token is acceptable.
 */
export async function getCaptchaToken() {
	const key = recaptchaSiteKey();
	if (!key) {
		return "";
	}
	try {
		const grecaptcha = await loadRecaptcha();
		if (!grecaptcha) {
			return "";
		}
		return await grecaptcha.execute(key, { action: ACTION });
	} catch (error) {
		console.error("reCAPTCHA token error:", error);
		return "";
	}
}

/** Headers to attach to backend requests. */
export async function captchaHeaders() {
	const token = await getCaptchaToken();
	return token ? { "X-IDG-Captcha": token } : {};
}
